import { type Dispatch, type FC, type SetStateAction } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Typography } from "@mui/material";
import { ConfirmationModal } from "@packsize/components";

import { useRemoveClose } from "../hooks/use-remove-close";

interface RemoveClosingModalProps {
	readonly internalUniqueId: string;
	readonly closingId: string;
	readonly alias: string;
	readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
	readonly isOpen: boolean;
}

export const RemoveClosingModal: FC<RemoveClosingModalProps> = ({
	internalUniqueId,
	closingId,
	alias,
	setIsOpen,
	isOpen,
}) => {
	const intl = useIntl();
	const removeCloseMutation = useRemoveClose(internalUniqueId, closingId);
	const handleConfirmRemove = () => {
		removeCloseMutation.mutate(undefined, {
			onSettled: () => {
				setIsOpen(false);
			},
		});
	};

	return (
		<ConfirmationModal
			title={intl.formatMessage({ id: "Remove closing?", defaultMessage: "Remove closing?" })}
			open={isOpen}
			onConfirm={handleConfirmRemove}
			onClose={() => setIsOpen(false)}
			onCancel={() => setIsOpen(false)}
			confirmText={intl.formatMessage({ id: "Remove closing", defaultMessage: "Remove closing" })}
			cancelText={intl.formatMessage({ id: "Go back", defaultMessage: "Go back" })}
			slotProps={{
				confirmButton: {
					loading: removeCloseMutation.isPending,
					"data-testid": "remove-closing-confirmation-modal__button__confirm",
				},
				cancelButton: {
					"data-testid": "remove-closing-confirmation-modal__button__cancel",
				},
			}}
		>
			<Typography>
				<FormattedMessage
					id="Are you sure you want to remove the closing for: {alias}?"
					defaultMessage="Are you sure you want to remove the closing for: {alias}?"
					values={{ alias }}
				/>
			</Typography>
		</ConfirmationModal>
	);
};
